<script context="module">
  import gql from 'graphql-tag';
  import { client } from 'utils/apollo.js';

  const PRODUCT = gql`
    query ($_eq: String!) {
      Product_ProductCollections(
        where: { slug: { _eq: $_eq }, visible: { _eq: true }, Product: { visible: { _eq: true } } }
        limit: 1
      ) {
        id
        name
        slug
        Product {
          id
          slug
          editableInDesigner
          isModelBasedImages
          useCoverImageForPreview
          mensAvailable
          price
          reversible
          womensAvailable
          youthAvailable
          displayColourPath
          printPositions
          isSublimationProduct
          ProductClipPath {
            id
            clipPaths
          }
          Product_PrintOptions(order_by: { printOptionId: asc }) {
            id
            PrintOption {
              id
              name
            }
          }
          ProductType {
            id
            name
          }
          Product_Colours(
            order_by: { ProductColoursMasterMain: { colourFamilyId: asc } }
            where: { visible: { _eq: true } }
          ) {
            id
            displayName
            pathName
            mens
            womens
            youth
            images
            ProductColoursMasterMain {
              id
              rgb
              name
            }
            ProductColoursMasterTrim1 {
              id
              name
              rgb
            }
            ProductColoursMasterTrim2 {
              id
              name
              rgb
            }
            ProductColoursMasterTrim3 {
              id
              name
              rgb
            }
          }
          Product_Sizes(order_by: { sizeId: asc }) {
            id
            width
            length
            units
            useAlternateSystem
            ProductSize {
              name
              gender
              id
            }
          }
          Product_DeliveryTimes {
            id
            DeliveryTime {
              id
              deliveryTime
              name
            }
          }
          Product_SublimationTemplates(where: { isVisible: { _eq: true } }) {
            id
            isDefault
            isVisible
            SublimationTemplate {
              id
              name
              numberOfColours
            }
          }
          BulkPricingDiscounts(order_by: { minQty: asc }) {
            id
            minQty
            maxQty
            discountPercent
          }
        }
        ProductCollection {
          id
          name
          slug
          ProductCategory {
            id
            name
            slug
          }
          Product_ProductCollections {
            id
            name
            slug
            visible
            ProductCollection {
              id
              name
              ProductCategory {
                id
                name
              }
            }
            Product {
              id
              slug
              price
              visible
              mensAvailable
              womensAvailable
              youthAvailable
              editableInDesigner
              displayColourPath
              isModelBasedImages
              useCoverImageForPreview
              printPositions
              isSublimationProduct
              ProductType {
                id
                name
              }
              ProductClipPath {
                id
                clipPaths
              }
              Product_PrintOptions {
                id
                PrintOption {
                  id
                  name
                }
              }
              Product_Colours(
                order_by: { ProductColoursMasterMain: { colourFamilyId: asc } }
                where: { visible: { _eq: true } }
              ) {
                id
                displayName
                pathName
                mens
                womens
                youth
                images
                ProductColoursMasterMain {
                  id
                  rgb
                  name
                }
                ProductColoursMasterTrim1 {
                  id
                  name
                  rgb
                }
                ProductColoursMasterTrim2 {
                  id
                  name
                  rgb
                }
                ProductColoursMasterTrim3 {
                  id
                  name
                  rgb
                }
              }
              Product_Sizes(order_by: { sizeId: asc }) {
                id
                width
                length
                units
                useAlternateSystem
                ProductSize {
                  id
                  name
                  gender
                }
              }
              Product_DeliveryTimes {
                id
                DeliveryTime {
                  id
                  deliveryTime
                  name
                }
              }
              Product_SublimationTemplates(where: { isVisible: { _eq: true } }) {
                id
                isDefault
                isVisible
                SublimationTemplate {
                  id
                  name
                  numberOfColours
                }
              }
              BulkPricingDiscounts(order_by: { minQty: asc }) {
                id
                minQty
                maxQty
                discountPercent
              }
            }
          }
        }
      }
      DesignerFonts(where: { visible: { _eq: true } }, order_by: { name: asc }) {
        id
        name
        svg
      }
      HeatTransferPrintColours(order_by: { displayOrder: asc }, where: { visible: { _eq: true } }) {
        id
        colour
        colourRgb
        isGlitter
        isMetallic
      }
    }
  `;

  async function queryProduct(productSlug) {
    const res = await client.query({
      query: PRODUCT,
      variables: {
        _eq: productSlug,
      },
    });
    return res.data;
  }

  export async function preload(page) {
    const { productSlug } = page.params;
    const dataRaw = await queryProduct(productSlug);
    const productDataRaw = dataRaw.Product_ProductCollections[0];
    const designerFonts = dataRaw.DesignerFonts;
    const heatTransferPrintColours = dataRaw.HeatTransferPrintColours;
    const displayColourPath = productDataRaw.Product.displayColourPath;

    // Re-Format Product Data
    // Use displayColourPath to find full details from colour data
    let selectedColour;
    if (displayColourPath) {
      selectedColour = productDataRaw.Product.Product_Colours.find((x) => x.pathName === displayColourPath);
    } else {
      selectedColour = productDataRaw.Product.Product_Colours[0];
    }

    let selectedSublimationTemplate = undefined;
    if (productDataRaw.Product.Product_SublimationTemplates.length > 0) {
      selectedSublimationTemplate =
        productDataRaw.Product.Product_SublimationTemplates.find((x) => x.isDefault) ||
        productDataRaw.Product.Product_SublimationTemplates[0];
    }

    let dataObject = {
      id: productDataRaw.id,
      name: productDataRaw.name,
      slug: productDataRaw.slug,
      rootSlug: productDataRaw.Product.slug,
      price: productDataRaw.Product.price,
      reversible: productDataRaw.Product.reversible,
      mensAvailable: productDataRaw.Product.mensAvailable,
      womensAvailable: productDataRaw.Product.womensAvailable,
      youthAvailable: productDataRaw.Product.youthAvailable,
      editableInDesigner: productDataRaw.Product.editableInDesigner,
      isModelBasedImages: productDataRaw.Product.isModelBasedImages,
      useCoverImageForPreview: productDataRaw.Product.useCoverImageForPreview,
      category: productDataRaw.ProductCollection.ProductCategory.name,
      collection: productDataRaw.ProductCollection.name,
      type: productDataRaw.Product.ProductType.name,
      clipPath: productDataRaw.Product?.ProductClipPath?.clipPaths,
      printPositions: productDataRaw.Product.printPositions,
      printOptions: productDataRaw.Product.Product_PrintOptions,
      colours: productDataRaw.Product.Product_Colours,
      sizes: productDataRaw.Product.Product_Sizes,
      selectedColour: selectedColour,
      deliveryTimes: productDataRaw.Product.Product_DeliveryTimes,
      isSublimationProduct: productDataRaw.Product.isSublimationProduct,
      sublimationTemplates: productDataRaw.Product.Product_SublimationTemplates,
      selectedSublimationTemplate: selectedSublimationTemplate,
      bulkDiscounts: productDataRaw.Product.BulkPricingDiscounts,
    };

    let productData = {};
    productData.top = null;
    productData.bottom = null;
    productData.hat = null;
    productData.socks = null;
    productData.accessories = null;

    switch (productDataRaw.Product.ProductType.name) {
      case 'top':
        productData.top = dataObject;
        productData.showing = 'top';
        break;
      case 'bottom':
        productData.bottom = dataObject;
        productData.showing = 'bottom';
        break;
      case 'hat':
        productData.hat = dataObject;
        productData.showing = 'hat';
        break;
      case 'socks':
        productData.socks = dataObject;
        productData.showing = 'socks';
        break;
      case 'accessories':
        productData.accessories = dataObject;
        productData.showing = 'accessories';
        break;

      default:
        break;
    }

    productData.collectionTops = [];
    productData.collectionBottoms = [];
    productData.collectionHats = [];
    productData.collectionSocks = [];
    productData.collectionAccessories = [];

    for (let product of productDataRaw.ProductCollection.Product_ProductCollections) {
      if (product.Product.visible === true && product.Product.editableInDesigner === true && product.visible === true) {
        if (product.Product.ProductType.name === 'top') {
          productData.collectionTops.push({ ...product, rootSlug: product.Product.slug });
        }
        if (product.Product.ProductType.name === 'bottom') {
          productData.collectionBottoms.push({ ...product, rootSlug: product.Product.slug });
        }
        if (product.Product.ProductType.name === 'hat') {
          productData.collectionHats.push({ ...product, rootSlug: product.Product.slug });
        }
        if (product.Product.ProductType.name === 'socks') {
          productData.collectionSocks.push({ ...product, rootSlug: product.Product.slug });
        }
        if (product.Product.ProductType.name === 'accessories') {
          productData.collectionAccessories.push({ ...product, rootSlug: product.Product.slug });
        }
      }
    }

    // check for collections with no items
    productData.topsAvailable = false;
    productData.bottomsAvailable = false;
    productData.hatsAvailable = false;
    productData.socksAvailable = false;
    productData.accessoriesAvailable = false;
    if (productData.collectionTops.length > 0) {
      productData.topsAvailable = true;
    }
    if (productData.collectionBottoms.length > 0) {
      productData.bottomsAvailable = true;
    }
    if (productData.collectionHats.length > 0) {
      productData.hatsAvailable = true;
    }
    if (productData.collectionSocks.length > 0) {
      productData.socksAvailable = true;
    }
    if (productData.collectionAccessories.length > 0) {
      productData.accessoriesAvailable = true;
    }

    // set selected gender
    if (productData[productData.showing].mensAvailable) {
      productData[productData.showing].gender = 'mens';
    } else if (productData[productData.showing].womensAvailable) {
      productData[productData.showing].gender = 'womens';
    } else if (productData[productData.showing].youthAvailable) {
      productData[productData.showing].gender = 'youth';
    } else {
      productData[productData.showing].gender = 'mens';
    }

    // Set side to top level - default front
    productData.side = 'front';

    // Set category and collection to top level
    productData.category = productDataRaw.ProductCollection.ProductCategory.name;
    productData.collection = productDataRaw.ProductCollection.name;
    productData.categorySlug = productDataRaw.ProductCollection.ProductCategory.slug;
    productData.collectionSlug = productDataRaw.ProductCollection.slug;

    return {
      productSlug,
      productData,
      designerFonts,
      heatTransferPrintColours,
    };
  }
</script>

<script>
  import { goto } from '@sapper/app';
  import { onMount, setContext } from 'svelte';

  import {
    designState,
    currentTab,
    activeElement,
    editingElement,
    isTablet,
    isMobile,
    designTabActive,
  } from '../../../stores/Designer.js';
  import { productDataStore } from '../../../stores/ProductData.js';
  import { konvaStageId, hidePreview } from '../../../stores/Preview.js';
  import { designerProductOverride } from '../../../stores/DesignerProductOverride.js';

  import Konva from 'konva';
  import { v4 as uuidv4 } from 'uuid';

  import {
    addDesignerImageToLayer,
    updateDesignerImage,
    toggleVisibleGroups,
    loadDesignFromState,
  } from './_design-functions.js';

  import TopNav from '../../../components/Design/TopNav.svelte';
  import StepNav from '../../../components/Design/StepNav.svelte';
  import ControlNav from '../../../components/Design/ControlNav.svelte';
  import ChooseUniformItems from '../../../components/Design/ChooseUniformItems.svelte';
  import ChooseColours from '../../../components/Design/ChooseColours.svelte';
  import TeamDetails from '../../../components/Design/TeamDetails.svelte';
  import ReviewOrder from '../../../components/Design/ReviewOrder.svelte';
  import Preview from '../../../components/Design/Preview.svelte';
  import Progress from '../../../components/Design/Progress.svelte';
  import PreviousDesigns from '../../../components/Design/PreviousDesigns.svelte';
  import { icons } from './_icons.js';
  import ChooseTemplate from '../../../components/Design/ChooseTemplate.svelte';

  export let segment;
  export let productSlug;

  export let productData; // We will pass product data to a store as it will change

  export let designerFonts; // Pass via context as will not change
  export let heatTransferPrintColours; // Pass via context as will not change

  setContext('designerFonts', designerFonts);
  setContext('heatTransferPrintColours', heatTransferPrintColours);

  $productDataStore = productData;

  // Redirect to start flow if someone links to /design/[productSlug] but does not include a step
  if (!segment && typeof window !== 'undefined') {
    goto('/design/' + productSlug + '/choose-uniform-items');
  }

  // Set active tab
  $: if (segment === 'front-print') {
    $currentTab = 'text';
  }

  $: if (segment === 'back-print') {
    $currentTab = 'names & no';
  }

  let showModal = false;

  const classicDesignSteps = [
    {
      stepNumber: 1,
      segment: 'choose-uniform-items',
      label: 'Choose Uniform Items',
    },
    {
      stepNumber: 2,
      segment: 'choose-colours',
      label: 'Choose Colours',
    },
    {
      stepNumber: 3,
      segment: 'front-print',
      label: 'Front Print',
    },
    {
      stepNumber: 4,
      segment: 'back-print',
      label: 'Back Print',
    },
    {
      stepNumber: 5,
      segment: 'team-details',
      label: 'Team Details',
    },
    {
      stepNumber: 6,
      segment: 'review-order',
      label: 'Review Order',
    },
  ];

  const sublimationDesignSteps = [
    {
      stepNumber: 1,
      segment: 'choose-uniform-items',
      label: 'Choose Uniform Items',
    },
    {
      stepNumber: 2,
      segment: 'choose-template',
      label: 'Choose Template',
    },
    {
      stepNumber: 3,
      segment: 'choose-colours',
      label: 'Choose Colours',
    },
    {
      stepNumber: 4,
      segment: 'front-print',
      label: 'Front Print',
    },
    {
      stepNumber: 5,
      segment: 'back-print',
      label: 'Back Print',
    },
    {
      stepNumber: 6,
      segment: 'team-details',
      label: 'Team Details',
    },
    {
      stepNumber: 7,
      segment: 'review-order',
      label: 'Review Order',
    },
  ];

  $: designSteps = $productDataStore[$productDataStore.showing].isSublimationProduct
    ? sublimationDesignSteps
    : classicDesignSteps;

  function determineCurrentStep(segment) {
    let step = designSteps.find((obj) => {
      return obj.segment === segment;
    });
    if (!step) {
      return designSteps[0]; // return first step if we cannot find step from segment
    }
    return step;
  }

  // Reactive so current step changes on change of segment
  $: currentStep = determineCurrentStep(segment);

  //   change class depending on steps to hide preview panel and make control panel wider for team-details and review steps
  $: controlFull = segment === 'team-details' || segment === 'review-order' ? true : false;

  $: if (segment !== 'review-order' || segment !== 'team-details') {
    $hidePreview = false;
  }

  // https://svelte.dev/repl/0ace7a508bd843b798ae599940a91783?version=3.16.7
  // Function to detect click outside of colour picker selectors - so we close them
  function clickOutside(node) {
    const handleClick = (event) => {
      if (node && !node.contains(event.target) && !event.defaultPrevented) {
        node.dispatchEvent(new CustomEvent('click_outside', node));
      }
    };
    document.addEventListener('click', handleClick, true);
    return {
      destroy() {
        document.removeEventListener('click', handleClick, true);
      },
    };
  }

  let konvaContainerReady = false;

  let konvaStage;
  let imgLayer;
  let designLayer;
  let frontTopDesignGroup;
  let backTopDesignGroup;
  let frontBottomDesignGroup;
  let backBottomDesignGroup;
  let frontHatDesignGroup;
  let backHatDesignGroup;
  let frontSocksDesignGroup;
  let backSocksDesignGroup;
  let frontAccessoryDesignGroup;
  let backAccessoryDesignGroup;

  let initialSetupComplete = false;

  let transformerDefaults = {
    centeredScaling: true,
    flipEnabled: false,
    rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315],
    rotateAnchorOffset: 30,
    resizeEnabled: true,
    borderStroke: '#FFBD08',
    borderStrokeWidth: 3,
    borderDash: [10, 10],
    enabledAnchors: ['bottom-right'],
  };
  let transformer = new Konva.Transformer(transformerDefaults);

  // Setup for show hide menu items on mobile table
  let showFloatingProgress = false;
  let mainHeight;
  let mainWidth;
  const tabletBreakPoint = 1000;
  const mobileBreakPoint = 700;

  $: if (mainWidth && mainWidth < tabletBreakPoint) {
    $isTablet = true;
    $isMobile = false;
  }

  $: if (mainWidth && mainWidth <= mobileBreakPoint) {
    $isTablet = false;
    $isMobile = true;
  }

  $: if (mainWidth && mainWidth >= tabletBreakPoint) {
    $isTablet = false;
    $isMobile = false;
  }

  // initial virtual stage heights - match product image dimensions
  const stageWidth = 512;
  const stageHeight = 625;

  function determineMainHeight() {
    // We want to set max-height of the preview-container and the window
    mainHeight = 700;
    if (typeof window !== 'undefined') {
      let documentHeight = window.innerHeight;
      let navHeight = document.querySelector('nav').offsetHeight;
      // We also have margin and the selected product heights 40, 20, 60

      mainHeight = documentHeight - navHeight;
    }
    return mainHeight;
  }

  function determineMainWidth() {
    if (typeof window !== 'undefined') {
      let documentWidth = window.innerWidth;

      mainWidth = documentWidth;
    }
    return mainWidth;
  }

  function initialiseKonva() {
    let stage = new Konva.Stage({
      container: 'preview-container',
      width: stageWidth,
      height: stageHeight,
      id: uuidv4(),
    });

    stage.on('click tap', function (event) {
      // if user clicks stage area
      // Remove nodes from transformers - e.g. hide them
      transformer.nodes([]);
      // backTransformer.nodes([]);
      // Set activeElement to null
      $activeElement = null;
      $editingElement = null;
      // Redraw layers
      designLayer.draw();
    });

    imgLayer = new Konva.Layer({
      name: 'img-layer',
    });

    designLayer = new Konva.Layer({
      name: 'design-layer',
    });

    // let clipPathCommands = JSON.parse(productData.clipPath)
    // TODO use the clip path values from the DB
    // Add designs into groups based on product types
    //We will use this to hide/show prints on top vs shorts, etc. as well as apply clip functions
    frontTopDesignGroup = new Konva.Group({
      //   clipFunc: create_clipping_function(
      //     clipPathCommands.mens.front
      //   ),
      name: 'front-design-top',
    });

    backTopDesignGroup = new Konva.Group({
      //   clipFunc: create_clipping_function(
      //     clipPathCommands.mens.back
      //   ),
      name: 'back-design-top',
    });
    frontBottomDesignGroup = new Konva.Group({
      //   clipFunc: create_clipping_function(
      //     clipPathCommands.mens.front
      //   ),
      name: 'front-design-bottom',
    });

    backBottomDesignGroup = new Konva.Group({
      //   clipFunc: create_clipping_function(
      //     clipPathCommands.heatTransfer.singlet.mens.back
      //   ),
      name: 'back-design-bottom',
    });
    frontHatDesignGroup = new Konva.Group({
      //   clipFunc: create_clipping_function(
      //     clipPathCommands.heatTransfer.singlet.mens.front
      //   ),
      name: 'front-design-hat',
    });

    backHatDesignGroup = new Konva.Group({
      //   clipFunc: create_clipping_function(
      //     clipPathCommands.heatTransfer.singlet.mens.back
      //   ),
      name: 'back-design-hat',
    });
    frontSocksDesignGroup = new Konva.Group({
      //   clipFunc: create_clipping_function(
      //     clipPathCommands.heatTransfer.singlet.mens.front
      //   ),
      name: 'front-design-socks',
    });

    backSocksDesignGroup = new Konva.Group({
      //   clipFunc: create_clipping_function(
      //     clipPathCommands.heatTransfer.singlet.mens.back
      //   ),
      name: 'back-design-socks',
    });
    frontAccessoryDesignGroup = new Konva.Group({
      //   clipFunc: create_clipping_function(
      //     clipPathCommands.heatTransfer.singlet.mens.front
      //   ),
      name: 'front-design-accessory',
    });

    backAccessoryDesignGroup = new Konva.Group({
      //   clipFunc: create_clipping_function(
      //     clipPathCommands.heatTransfer.singlet.mens.back
      //   ),
      name: 'back-design-accessory',
    });

    // Handle clicks to the design layer
    designLayer.on('click tap', function (event) {
      if (!(event.target instanceof Konva.Transformer)) {
        // if transformer do nothing - click to transformer handled in transformer callback
        // otherwise show transformer
        transformer.nodes([event.target]);
      }
      this.draw();
      event.cancelBubble = true; //Don't bubble to stage
    });

    stage.add(imgLayer);
    stage.add(designLayer);
    designLayer.add(transformer);
    designLayer.add(frontTopDesignGroup);
    designLayer.add(frontBottomDesignGroup);
    designLayer.add(frontHatDesignGroup);
    designLayer.add(frontSocksDesignGroup);
    designLayer.add(frontAccessoryDesignGroup);
    designLayer.add(backTopDesignGroup);
    designLayer.add(backBottomDesignGroup);
    designLayer.add(backHatDesignGroup);
    designLayer.add(backSocksDesignGroup);
    designLayer.add(backAccessoryDesignGroup);

    return stage;
  }

  // variable to hold data for modal if on loading there are previous designs detected
  let previousDesigns;

  function previousDesignSummary(state) {
    let summaryArray = [];
    for (const design of state) {
      let product;
      let productType;

      // set if statements in reverse order of importance
      // e.g. if they have a top then show that over a bottom
      if (design.accessory.product) {
        product = design.accessory;
        productType = 'accessory';
      }

      if (design.socks.product) {
        product = design.socks;
        productType = 'socks';
      }

      if (design.hat.product) {
        product = design.hat;
        productType = 'hat';
      }

      if (design.bottom.product) {
        product = design.bottom;
        productType = 'bottom';
      }

      if (design.top.product) {
        product = design.top;
        productType = 'top';
      }
      let summary = {
        id: design.id,
        type: productType,
        product: product,
      };

      summaryArray.push(summary);
    }

    return summaryArray;
  }

  function productAlreadyUsedInDesigner(state, slug) {
    let alreadyUsed = false;
    let usedProductType = null;
    let productsToLoad = [];

    if (state.top.product) {
      if (state.top.product.slug === slug) {
        alreadyUsed = true;
        usedProductType = 'top';
      } else {
        productsToLoad.push('top');
      }
    }
    if (state.bottom.product) {
      if (state.bottom.product.slug === slug) {
        alreadyUsed = true;
        usedProductType = 'bottom';
      } else {
        productsToLoad.push('bottom');
      }
    }
    if (state.hat.product) {
      if (state.hat.product.slug === slug) {
        alreadyUsed = true;
        usedProductType = 'hat';
      } else {
        productsToLoad.push('hat');
      }
    }
    if (state.socks.product) {
      if (state.socks.product.slug === slug) {
        alreadyUsed = true;
        usedProductType = 'socks';
      } else {
        productsToLoad.push('socks');
      }
    }
    if (state.accessory.product) {
      if (state.accessory.product.slug === slug) {
        alreadyUsed = true;
        usedProductType = 'accessory';
      } else {
        productsToLoad.push('accessory');
      }
    }

    return {
      alreadyUsed,
      usedProductType,
      productsToLoad,
    };
  }

  function addToKonva(image, details) {
    // set designTabActive to false to hide tab menu in mobile
    $designTabActive = false;
    let groupName = determineGroupName();

    addDesignerImageToLayer(konvaStage, image, $productDataStore.side, groupName, details, transformer);
  }

  function updateInKonva(existingElement, image, details) {
    let side = $productDataStore.side;
    // use segment to determine side

    updateDesignerImage(konvaStage, existingElement, image, side, details, transformer);
  }

  function resize() {
    // reset mainHeight on window size change
    // delta is a hack to try and get preview to show
    mainHeight = determineMainHeight();
    mainWidth = determineMainWidth();
  }

  function determineGroupName() {
    let groupName;
    if ($productDataStore.side === 'front') {
      groupName = 'front-design-' + $productDataStore.showing;
    }
    if ($productDataStore.side === 'back') {
      groupName = 'back-design-' + $productDataStore.showing;
    }
    if ($productDataStore.side === 'rightSide') {
      groupName = 'rightSide-design-' + $productDataStore.showing;
    }
    if ($productDataStore.side === 'leftSide') {
      groupName = 'leftSide-design-' + $productDataStore.showing;
    }
    return groupName;
  }

  let transformerActive = false;
  $: if (transformer.nodes().length > 0) {
    transformerActive = true;
  }

  // function to toggle visible groups in designer
  $: if ($konvaStageId && konvaStage && $productDataStore) {
    let groupName = determineGroupName();
    toggleVisibleGroups(konvaStage, groupName);
  }

  // Once preview container has been added to DOM - initialise Konva
  let initialiseComplete = false; // Run once only
  $: if (!initialiseComplete && konvaContainerReady) {
    konvaStage = initialiseKonva();

    // set clip path for selected product
    // Disable clip path on all products
    // updateProductClipPath(konvaStage, $productDataStore);

    // Set/Parse designState - add product and default colour
    // design state store should exist here
    if ($designState && $designState.length === 1 && !designState.stateIsEmpty()) {
      // You have started one previous design and design state has data
      let designStateData = $designState[0];

      // If the product url and colour are the same then load design without popup
      let { alreadyUsed, usedProductType, productsToLoad } = productAlreadyUsedInDesigner(designStateData, productSlug);

      // Check if there are any design elements - e.g. print items.
      let designElementsExist = designState.designElementsExist();

      if (alreadyUsed) {
        // load previous state
        konvaStage.id(designStateData.id); // set stage id to match state
        $konvaStageId = konvaStage.id(); // Set id of current stage to store.

        // Show same product type as matching slug
        $productDataStore.showing = usedProductType;

        // Set colour to match that from designState
        $productDataStore[usedProductType].selectedColour = designStateData[usedProductType].colour;

        // Set sublimation template to match that from designState
        $productDataStore[usedProductType].selectedSublimationTemplate =
          designStateData[usedProductType].sublimationTemplate;

        // Need to set additional products and their colours
        // e.g. shorts, socks, etc
        for (const productType of productsToLoad) {
          let collectionName;
          switch (productType) {
            case 'top':
              collectionName = 'collectionTops';
              break;
            case 'bottom':
              collectionName = 'collectionBottoms';
              break;
            case 'hat':
              collectionName = 'collectionHats';
              break;
            case 'socks':
              collectionName = 'collectionSocks';
              break;
            case 'accessory':
              collectionName = 'collectionAccessories';
              break;

            default:
              break;
          }
          // Get product from design state
          // Use slug here to match as it is backwards compatible with old data
          let productSlug = designStateData[productType].product.slug;

          // Get product data from data store collection data
          let productCollection = $productDataStore[collectionName];

          // get product data from data store
          let productProductCollection = productCollection.find((o) => o.slug === productSlug);
          if (!productProductCollection) {
            designState.deleteProduct($konvaStageId, productType); // TODO - Can we keep design data or show closest matched product?
            continue;
          }

          let gender = $productDataStore[$productDataStore.showing].gender;

          let selectedColour = designStateData[productType].colour;

          let selectedSublimationTemplate = designStateData[productType].sublimationTemplate;
          // TODO - Beware -data structure here must match that set up in _layout preload and choose uniform items
          let productData = {
            id: productProductCollection.id,
            name: productProductCollection.name,
            slug: productProductCollection.slug,
            rootSlug: productProductCollection.Product.slug,
            price: productProductCollection.Product.price,
            reversible: productProductCollection.Product.reversible,
            mensAvailable: productProductCollection.Product.mensAvailable,
            womensAvailable: productProductCollection.Product.womensAvailable,
            youthAvailable: productProductCollection.Product.youthAvailable,
            editableInDesigner: productProductCollection.Product.editableInDesigner,
            isModelBasedImages: productProductCollection.Product.isModelBasedImages,
            useCoverImageForPreview: productProductCollection.Product.useCoverImageForPreview,
            category: productProductCollection.ProductCollection.ProductCategory.name,
            collection: productProductCollection.ProductCollection.name,
            type: productProductCollection.Product.ProductType.name,
            clipPath: productProductCollection.Product?.ProductClipPath?.clipPaths,
            printOptions: productProductCollection.Product.Product_PrintOptions,
            colours: productProductCollection.Product.Product_Colours,
            sizes: productProductCollection.Product.Product_Sizes,
            selectedColour: selectedColour,
            deliveryTimes: productProductCollection.Product.Product_DeliveryTimes,
            gender: gender, // Copy across current gender setting from what was previously showing
            isSublimationProduct: productProductCollection.Product.isSublimationProduct,
            sublimationTemplates: productProductCollection.Product.Product_SublimationTemplates,
            selectedSublimationTemplate: selectedSublimationTemplate,
          };

          $productDataStore[productType] = productData;
        }

        if (designElementsExist) {
          // Add all the design elements back
          for (const productType of [usedProductType, ...productsToLoad]) {
            let designElements = $designState[0][productType].designElements;

            // add design elements to item
            loadDesignFromState(konvaStage, designElements, productType, transformer);
          }
        }
      } else if (!designElementsExist) {
        // No design elements exist, but trying to load new product - load new product without popup
        // set id in design state to match stage id

        // TODO - We need to handle sitation here where user is changing product type
        // E.g. shorts to top or similar. In this case the productDataStore values will be different?
        designState.clearDesignState();
        $konvaStageId = konvaStage.id();
        designState.addNewDesign($konvaStageId);

        // TODO - should set defaults based on productData here.
        // selected colour should be set to incoming product default
        // selected sublimation template should be set to incoming product default

        designState.addProduct(
          $konvaStageId,
          $productDataStore[$productDataStore.showing],
          $productDataStore[$productDataStore.showing].selectedColour,
          $productDataStore[$productDataStore.showing].selectedSublimationTemplate,
        );
      } else if ($designerProductOverride) {
        // If designer product override is set then we want to load the new product and add the design elements back.
        // first check if the product type matches the one in the design state
        // TODO - Need to handle the situation where they change collectiona and/or product type.

        let productType = $productDataStore.showing;

        // Get the id from the state
        let stateId = $designState[0].id;

        // set the value to the stage id
        konvaStage.id(stateId);
        $konvaStageId = konvaStage.id(); // Set id of current stage to store.

        // Add the product to the design state
        designState.addProduct(
          $konvaStageId,
          $productDataStore[$productDataStore.showing],
          $productDataStore[$productDataStore.showing].selectedColour,
          $productDataStore[$productDataStore.showing].selectedSublimationTemplate,
        );

        // TODO - currently only need to add the design elements back for this productType as we delete the other types.
        if (designElementsExist) {
          let designElements = $designState[0][$productDataStore.showing].designElements;

          // add design elements to item
          loadDesignFromState(konvaStage, designElements, productType, transformer);
        }

        // Here we just delete all the other product types.
        if (productType !== 'top') {
          designState.deleteProduct($konvaStageId, 'top');
        }
        if (productType !== 'bottom') {
          designState.deleteProduct($konvaStageId, 'bottom');
        }
        if (productType !== 'hat') {
          designState.deleteProduct($konvaStageId, 'hat');
        }
        if (productType !== 'socks') {
          designState.deleteProduct($konvaStageId, 'socks');
        }
        if (productType !== 'accessory') {
          designState.deleteProduct($konvaStageId, 'accessory');
        }

        $designerProductOverride = false;
      } else {
        // Product URL does not match url from saved state - i.e. trying to adding a new product
        // And previous design elements exist - so show popup to user.

        // If there is a new product but no design elements then we do not need to show popup and just switch product
        konvaStage.id(designStateData.id);
        $konvaStageId = konvaStage.id();

        // Use previousDesign function to determine product type of previous design
        previousDesigns = previousDesignSummary($designState);

        // show popup
        showModal = true;
      }
    } else if ($designState && $designState.length === 1 && designState.stateIsEmpty()) {
      // You have started one previous design but the state has no data
      // This can happen if you start designing one product type and then try to load again with another product type
      // This is a new product
      // Need to Add product data back again
      // First need to check if the designState id and stage id match, they should?
      $konvaStageId = konvaStage.id(); //Set id of current stage to store.
      let stateId = $designState[0].id;
      if (stateId === $konvaStageId) {
        designState.addProduct(
          $konvaStageId,
          $productDataStore[$productDataStore.showing],
          $productDataStore[$productDataStore.showing].selectedColour,
          $productDataStore[$productDataStore.showing].selectedSublimationTemplate,
        );
      } else {
        // No previous design started
        // set id in design state to match stage id
        designState.addProduct(
          $konvaStageId,
          $productDataStore[$productDataStore.showing],
          $productDataStore[$productDataStore.showing].selectedColour,
          $productDataStore[$productDataStore.showing].selectedSublimationTemplate,
          true,
        );
      }

      designState.addProduct(
        $konvaStageId,
        $productDataStore[$productDataStore.showing],
        $productDataStore[$productDataStore.showing].selectedColour,
        $productDataStore[$productDataStore.showing].selectedSublimationTemplate,
      );
    } else if (!$designState || $designState.length === 0) {
      // No pre-existing designs
      if (!$designState || $designState.length === 0) {
        designState.addNewDesign(konvaStage.id()); // Use Id from konva stage
        $konvaStageId = konvaStage.id(); // Save the id of current stage to the store.

        // Add product
        designState.addProduct(
          $konvaStageId,
          $productDataStore[$productDataStore.showing],
          $productDataStore[$productDataStore.showing].selectedColour,
          $productDataStore[$productDataStore.showing].selectedSublimationTemplate,
        );
      }
    } else {
      // Something else - clear design state and start again
      designState.addNewDesign(konvaStage.id()); // Use Id from konva stage
      $konvaStageId = konvaStage.id(); // Set id of current stage to store.

      // Add product
      designState.addProduct(
        $konvaStageId,
        $productDataStore[$productDataStore.showing],
        $productDataStore[$productDataStore.showing].selectedColour,
        $productDataStore[$productDataStore.showing].selectedSublimationTemplate,
      );
    }

    initialiseComplete = true;
  }

  onMount(() => {
    mainHeight = determineMainHeight();
    mainWidth = determineMainWidth();
  });
</script>

<PreviousDesigns bind:showModal {previousDesigns} />

<svelte:window on:resize={resize} />
<TopNav {designSteps} {segment} {currentStep} bind:showFloatingProgress />

<div class="main" style="--height: {mainHeight}px">
  <div class="progress">
    <Progress
      {designSteps}
      productName={productData[productData.showing].name}
      productSlug={productData[productData.showing].slug}
      bind:currentStep
    />
  </div>

  <!-- Don't generate preview if modal is displayed - wait until after user makes selection in modal -->
  {#if !showModal}
    <div class="preview" class:hide={$hidePreview}>
      <!-- make this conditional to force svelte to remount the preview when switching segments -->
      {#if segment === 'front-print' || segment === 'back-print' || segment === 'choose-uniform-items' || segment === 'choose-template' || segment === 'choose-colours' || segment === 'team-details' || segment === 'review-order'}
        <Preview
          {stageWidth}
          {stageHeight}
          {currentTab}
          bind:segment
          bind:mainHeight
          bind:mainWidth
          bind:konvaContainerReady
          bind:konvaStage
          bind:initialSetupComplete
        />
      {/if}
    </div>
  {/if}

  <div
    class="control"
    class:full={controlFull}
    class:fit-content={$isMobile && !$designTabActive}
    class:mobile-z-index={($isMobile && segment === 'review-order') || ($isMobile && segment === 'team-details')}
  >
    <StepNav {icons} bind:currentStep {designSteps} />
    {#if segment === 'front-print' || segment === 'back-print'}
      <ControlNav {addToKonva} {updateInKonva} {icons} {currentTab} {konvaStage} {segment} />
    {/if}
    {#if segment === 'choose-uniform-items'}
      <ChooseUniformItems resizeFunction={resize} />
    {/if}
    {#if segment === 'choose-template'}
      <ChooseTemplate resizeFunction={resize} />
    {/if}
    {#if segment === 'choose-colours'}
      <!-- pass in the stage to stop it being removed? -->
      <ChooseColours resizeFunction={resize} />
    {/if}
    {#if segment === 'team-details'}
      <!-- pass in the stage to stop it being removed? -->
      <TeamDetails bind:konvaStage />
    {/if}
    {#if segment === 'review-order'}
      <!-- pass in the stage to stop it being removed? -->
      <ReviewOrder bind:konvaStage />
    {/if}
    <slot />
  </div>
</div>

<!-- Outside of main grid nav - show when click on details price indicator -->
{#if showFloatingProgress}
  <div class="progress-floating">
    <Progress
      {designSteps}
      productName={productData[productData.showing].name}
      productSlug={productData[productData.showing].slug}
      bind:currentStep
      bind:showFloatingProgress
    />
  </div>
{/if}

<style>
  .main {
    display: grid;
    grid-template-columns: minmax(0, 260px) minmax(0, 1fr) minmax(0, 350px);
    grid-gap: 2rem;
    background-color: #f7f8fa;
    height: var(--height);
    /* height: auto; */
  }

  .preview {
    display: block;
    height: unset;
  }

  .preview.hide {
    display: none;
  }
  .control {
    height: -moz-fit-content;
    height: fit-content;
    /* height: auto; */
    background: none;
    margin: 2rem;
    margin-left: 0rem;
    background-color: #fff;
    border-radius: 5px;
    box-shadow: 1px 1px 0.5rem rgba(0, 0, 0, 0.15);
    max-height: var(--height);
    overflow-y: auto;
    padding-right: 1.2rem;
  }
  .control.full {
    grid-column: 2/-1;
  }

  .progress {
    margin: 2rem;
    margin-right: 0rem;
    overflow-y: auto;
  }

  .progress-floating {
    position: absolute;
    top: 56px;
    right: 2rem;
    z-index: 200;
  }

  @media screen and (min-width: 1400px) {
    .main {
      grid-template-columns: minmax(0, 350px) minmax(0, 1fr) minmax(0, 450px);
    }
  }

  @media (min-width: 1000px) and (max-width: 1200px) {
    .main {
      grid-template-columns: minmax(0, 260px) minmax(0, 1fr) minmax(0, 350px);
    }
  }

  @media screen and (max-width: 1000px) {
    .progress {
      display: none;
    }
    .main {
      grid-template-columns: minmax(0, 1fr) minmax(0, 350px);
    }
    .control.full {
      grid-column: 1/-1;
    }
  }

  @media screen and (max-width: 700px) {
    .progress {
      display: none;
      grid-area: 1/1;
      z-index: 20;
    }
    .main {
      grid-template-columns: minmax(0, 1fr);
    }
    .control {
      /* Remove fit-content so control area covers entire screen - i.e don't see design underneath */
      height: unset;
      grid-area: 1/1;
      /* There is a bug in safari that stops the position:fixed stepnav div from showing if this parent div has z-index */
      z-index: 10;
      margin: 0rem;
    }

    .control.mobile-z-index {
      z-index: auto;
    }

    .control.fit-content {
      height: -moz-fit-content;
      height: fit-content;
      /* height: auto; */
    }

    .preview {
      display: block;
      margin-top: 0;
      grid-area: 1/1;
    }

    .preview.hide {
      display: none;
    }

    /* .preview.mobile-hidden {
      display: none;
    } */
  }
</style>
